import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeftIcon, XIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { createSubmitOnEnterKeyDownHandler } from '@/utils/keyboard';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ModalPortalContainerContext } from './modalPortalContainer';

export enum ModalVariant {
    DEFAULT = 'default',
    PLAIN = 'plain',
}

export enum ModalActionType {
    DEFAULT = 'default',
    DANGER = 'danger',
    SECONDARY = 'secondary',
}

type ModalSize = 'small' | 'medium' | 'large';

const MODAL_MAX_WIDTH_BY_SIZE: Record<ModalSize, string> = {
    small: 'max-w-[480px]',
    medium: 'max-w-[640px]',
    large: 'max-w-[1000px]',
};

interface ModalProps {
    isOpen: boolean;
    onClose?: () => void;
    title?: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    onBack?: () => void;
    onCancel?: () => void;
    onSubmit?: () => void;
    submitText?: string;
    submitDisabled?: boolean;
    submitInProgress?: boolean;
    secondaryButtonText?: string;
    onSecondarySubmit?: () => void;
    secondaryButtonDisabled?: boolean;
    size?: ModalSize;
    maxWidth?: string;
    maxHeight?: string;
    variant?: ModalVariant;
    actionType?: ModalActionType;
    fullHeight?: boolean;
    submitOnEnter?: boolean;
    contentScrollUnderFooter?: boolean;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
    closeOnOutsideClick?: boolean;
    submitButtonTestId?: string;
    contentTestId?: string;
    closeButtonTestId?: string;
    submitButtonTrailingIcon?: React.ReactNode;
    submitButtonFullWidth?: boolean;
    onOpenAutoFocus?: (event: Event) => void;
}

export function Modal({
    isOpen,
    onClose,
    title,
    description,
    children,
    onBack,
    onCancel,
    onSubmit,
    submitText,
    submitOnEnter = true,
    submitDisabled = false,
    submitInProgress = false,
    secondaryButtonText,
    onSecondarySubmit,
    secondaryButtonDisabled = false,
    size = 'large',
    maxWidth,
    maxHeight,
    variant = ModalVariant.DEFAULT,
    actionType = ModalActionType.DEFAULT,
    fullHeight = false,
    contentScrollUnderFooter = false,
    scrollContainerRef,
    closeOnOutsideClick = false,
    submitButtonTestId,
    contentTestId,
    closeButtonTestId,
    submitButtonTrailingIcon,
    submitButtonFullWidth = false,
    onOpenAutoFocus,
}: ModalProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const [contentElement, setContentElement] =
        React.useState<HTMLDivElement | null>(null);
    const blockOverlayDismiss = !closeOnOutsideClick || submitInProgress;
    const resolvedSubmitText = submitText ?? t('button.ok');
    const isSingleFullWidthSubmit =
        submitButtonFullWidth && onSubmit && !onCancel && !secondaryButtonText;
    const resolvedMaxWidth = maxWidth ?? MODAL_MAX_WIDTH_BY_SIZE[size];

    const footerContent =
        onCancel || onSubmit ? (
            <>
                {variant === ModalVariant.DEFAULT && (
                    <div className="h-px w-full bg-grey-500 shrink-0" />
                )}
                <div
                    className={cn(
                        'flex items-center justify-between gap-3',
                        variant === ModalVariant.PLAIN
                            ? 'px-6 pb-6 pt-0'
                            : 'pt-6 px-6 pb-6',
                    )}
                >
                    {onCancel && (
                        <Button variant="ghost" onClick={onCancel}>
                            {t('button.cancel')}
                        </Button>
                    )}
                    <div
                        className={cn(
                            'flex items-center gap-3',
                            isSingleFullWidthSubmit ? 'w-full' : 'ml-auto',
                        )}
                    >
                        {secondaryButtonText && onSecondarySubmit && (
                            <Button
                                variant="secondary"
                                onClick={onSecondarySubmit}
                                disabled={secondaryButtonDisabled}
                            >
                                {secondaryButtonText}
                            </Button>
                        )}
                        {onSubmit && (
                            <Button
                                variant={
                                    actionType === ModalActionType.DANGER
                                        ? 'danger'
                                        : actionType ===
                                            ModalActionType.SECONDARY
                                          ? 'secondary'
                                          : 'primary'
                                }
                                onClick={onSubmit}
                                disabled={submitDisabled}
                                loading={submitInProgress}
                                data-testid={submitButtonTestId}
                                trailingIcon={submitButtonTrailingIcon}
                                className={cn(
                                    isSingleFullWidthSubmit && 'w-full',
                                )}
                            >
                                {resolvedSubmitText}
                            </Button>
                        )}
                    </div>
                </div>
            </>
        ) : null;

    return (
        <DialogPrimitive.Root
            open={isOpen}
            onOpenChange={(open) => {
                if (!open && closeOnOutsideClick && !submitInProgress) {
                    onCancel?.();
                }
            }}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className={cn(
                        'fixed inset-0 z-50 bg-black-100/80 backdrop-blur-md',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'duration-100',
                    )}
                />
                <DialogPrimitive.Content
                    ref={setContentElement}
                    className={cn(
                        'fixed left-[50%] top-[50%] z-50 w-full max-h-[calc(100dvh-2rem)] @container',
                        '-translate-x-1/2 -translate-y-1/2',
                        'origin-center',
                        resolvedMaxWidth,
                        'transition-[opacity,transform,max-width] duration-180 ease-[cubic-bezier(0.22,1,0.36,1)]',
                        'data-[state=open]:opacity-100 data-[state=open]:scale-100',
                        'data-[state=closed]:opacity-0 data-[state=closed]:scale-[0.97]',
                    )}
                    data-testid={contentTestId}
                    aria-describedby={undefined}
                    onOpenAutoFocus={onOpenAutoFocus}
                    onEscapeKeyDown={(e) =>
                        blockOverlayDismiss && e.preventDefault()
                    }
                    onPointerDownOutside={(e) =>
                        blockOverlayDismiss && e.preventDefault()
                    }
                    onInteractOutside={(e) =>
                        blockOverlayDismiss && e.preventDefault()
                    }
                    onKeyDown={createSubmitOnEnterKeyDownHandler({
                        submitOnEnter,
                        onSubmit,
                        submitDisabled,
                        submitInProgress,
                    })}
                >
                    <ModalPortalContainerContext.Provider
                        value={contentElement}
                    >
                        <div
                            className={cn(
                                'bg-grey-700 rounded-[10px] overflow-hidden border border-grey-500 flex flex-col transition-[max-height,height] duration-180 ease-[cubic-bezier(0.22,1,0.36,1)]',
                                maxHeight ??
                                    (fullHeight
                                        ? 'h-[calc(100vh-280px)]'
                                        : 'max-h-[calc(100vh-280px)]'),
                            )}
                        >
                            <div className="flex-shrink-0">
                                {variant === ModalVariant.PLAIN &&
                                description ? (
                                    <div className="flex flex-col gap-2 px-6 pt-6 pb-6">
                                        {title ? (
                                            <DialogPrimitive.Title className="text-white-100 text-md-medium">
                                                {title}
                                            </DialogPrimitive.Title>
                                        ) : null}
                                        <div className="text-grey-300 text-sm leading-5">
                                            {description}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between px-6 py-6">
                                            {title ? (
                                                <div className="flex items-center gap-2">
                                                    {onBack && (
                                                        <Button
                                                            variant="ghost"
                                                            onClick={onBack}
                                                            className="text-white-100 hover:text-white-100"
                                                            leadingIcon={
                                                                <ArrowLeftIcon className="w-5 h-5" />
                                                            }
                                                            aria-label={t(
                                                                'modal.back',
                                                            )}
                                                        />
                                                    )}
                                                    <DialogPrimitive.Title className="text-white-100 text-md-medium">
                                                        {title}
                                                    </DialogPrimitive.Title>
                                                </div>
                                            ) : (
                                                <div />
                                            )}
                                            {onClose && (
                                                <DialogPrimitive.Close asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={onClose}
                                                        className="text-grey-200 hover:text-white-100"
                                                        leadingIcon={
                                                            <XIcon className="h-4 w-4" />
                                                        }
                                                        aria-label={t(
                                                            'modal.close',
                                                        )}
                                                        data-testid={
                                                            closeButtonTestId
                                                        }
                                                    />
                                                </DialogPrimitive.Close>
                                            )}
                                        </div>
                                        {variant === ModalVariant.DEFAULT && (
                                            <div className="h-px w-full bg-grey-500" />
                                        )}
                                    </>
                                )}
                            </div>
                            {contentScrollUnderFooter && footerContent ? (
                                <div className="flex-1 min-h-0 relative flex flex-col">
                                    <div
                                        ref={scrollContainerRef}
                                        className="flex-1 min-h-0 overflow-y-auto pb-24 isolate"
                                    >
                                        {children}
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 z-30 bg-grey-700 flex flex-col">
                                        {footerContent}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {!(
                                        variant === ModalVariant.PLAIN &&
                                        description &&
                                        React.Children.count(
                                            children ?? null,
                                        ) === 0
                                    ) && (
                                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                                                {children}
                                            </div>
                                        </div>
                                    )}
                                    {footerContent && (
                                        <div className="flex-shrink-0">
                                            {footerContent}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </ModalPortalContainerContext.Provider>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
